import React from 'react';
import { SparklesIcon } from 'lucide-react';
import { motion } from 'framer-motion';
export function Hero() {
  return <div className="relative w-full flex flex-col items-center justify-center px-6 py-8 overflow-hidden" data-id="element-32">
      {/* Animated gradient overlay */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 2
    }} className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-900/30 to-cyan-900/30" data-id="element-33" />
      {/* Animated grid pattern */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 1.5,
      delay: 0.3
    }} className="absolute inset-0 bg-[linear-gradient(rgba(139,92,246,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(139,92,246,0.05)_1px,transparent_1px)] bg-[size:50px_50px]" data-id="element-34" />
      {/* Spotlight effects */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-[120px] animate-pulse" data-id="element-35" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-[120px] animate-pulse" style={{
      animationDelay: '1s'
    }} data-id="element-36" />
      {/* Logo */}
      <motion.div initial={{
      opacity: 0,
      y: -50
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 1,
      ease: 'easeOut'
    }} className="relative z-20 flex items-center gap-4 mb-6" data-id="element-37">
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/eJEakYEU2Q5KKFpNx8UvgD/PSI_Banner_Logo_PNG_Transparent.png" alt="Peptide Science Institute Logo" className="h-12 md:h-16 drop-shadow-2xl" data-id="element-38" />
      </motion.div>
      {/* Exclusive Badge */}
      <motion.div initial={{
      opacity: 0,
      scale: 0.8
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      delay: 0.5,
      duration: 0.6
    }} className="relative z-20 mb-6" data-id="element-39"></motion.div>
      {/* Main Title Box */}
      <motion.div initial={{
      opacity: 0,
      y: 50
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.8,
      duration: 0.8
    }} className="relative z-20 w-full max-w-5xl mx-auto mb-8" data-id="element-40">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/30 via-cyan-600/30 to-purple-600/30 rounded-3xl blur-3xl animate-pulse" data-id="element-41" />
        <div className="relative bg-gradient-to-br from-slate-900/95 via-purple-950/95 to-slate-900/95 border-2 border-purple-400/40 rounded-3xl p-8 md:p-12 backdrop-blur-md shadow-2xl" data-id="element-42">
          <div className="text-center space-y-4" data-id="element-43">
            <motion.h1 initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 1.2,
            duration: 1
          }} className="space-y-3" style={{
            fontFamily: 'Oswald, sans-serif'
          }} data-id="element-44">
              <div className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-black bg-gradient-to-r from-cyan-300 via-purple-300 to-cyan-300 bg-clip-text text-transparent leading-tight uppercase" style={{
              textShadow: '0 0 120px rgba(34, 211, 238, 0.9), 0 0 60px rgba(168, 85, 247, 0.7)',
              letterSpacing: '0.03em'
            }} data-id="element-45">
                Meeting With the Master
              </div>
              <div className="h-2 w-full bg-gradient-to-r from-transparent via-cyan-400 to-transparent rounded-full my-3 shadow-2xl shadow-cyan-500/60" data-id="element-46" />
              <div className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-extrabold bg-gradient-to-r from-white via-purple-100 to-cyan-100 bg-clip-text text-transparent leading-tight uppercase tracking-wide" style={{
              textShadow: '0 0 100px rgba(139, 92, 246, 0.8), 0 0 50px rgba(139, 92, 246, 0.6)',
              letterSpacing: '0.02em'
            }} data-id="element-47">
                "Change Your Life In A Single Hour"
              </div>
            </motion.h1>
            <motion.div initial={{
            scaleX: 0
          }} animate={{
            scaleX: 1
          }} transition={{
            delay: 1.5,
            duration: 0.8
          }} className="h-1 w-48 mx-auto bg-gradient-to-r from-transparent via-cyan-400 to-transparent rounded-full shadow-lg shadow-cyan-500/50" data-id="element-48" />
          </div>
        </div>
      </motion.div>
      <motion.div initial={{
      opacity: 0,
      y: 30
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 1.6,
      duration: 0.8
    }} className="relative z-10 max-w-5xl mx-auto text-center space-y-8" data-id="element-49">
        <p className="text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed font-light drop-shadow-lg" data-id="element-50">
          No more messing around. It's time for you to FINALLY get the right
          answers.
        </p>
        {/* Masters Introduction */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 1.8,
        duration: 0.8
      }} className="grid md:grid-cols-2 gap-6 py-6" data-id="element-51">
          {/* Dr. Ali Mazloum */}
          <div className="relative bg-gradient-to-br from-purple-900/40 via-cyan-900/30 to-purple-900/40 border-2 border-cyan-500/40 rounded-xl p-6 backdrop-blur-sm" data-id="element-52">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-cyan-600/10 blur-2xl rounded-xl pointer-events-none" data-id="element-53" />
            <div className="relative space-y-3 text-center" data-id="element-54">
              <div className="flex justify-center mb-4" data-id="element-55">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/r3ytD56TFByCqkHoB8LFzP/DrAli1.png" alt="Dr. Ali Mazloum" className="w-20 h-20 md:w-28 md:h-28 rounded-full object-cover border-4 border-cyan-500/50 shadow-lg shadow-cyan-500/30" data-id="element-56" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-white" style={{
              fontFamily: 'Oswald, sans-serif'
            }} data-id="element-57">
                Dr. Ali Mazloum
              </h3>
              <p className="text-base md:text-lg font-semibold text-cyan-400" data-id="element-58">
                The Human MRI
              </p>
            </div>
          </div>
          {/* Brenden Henry */}
          <div className="relative bg-gradient-to-br from-purple-900/40 via-cyan-900/30 to-purple-900/40 border-2 border-cyan-500/40 rounded-xl p-6 backdrop-blur-sm" data-id="element-59">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-cyan-600/10 blur-2xl rounded-xl pointer-events-none" data-id="element-60" />
            <div className="relative space-y-3 text-center" data-id="element-61">
              <div className="flex justify-center mb-4" data-id="element-62">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/vptGHfonavjt96KY7KsnCE/Image_2.jpg" alt="Brenden Henry" className="w-20 h-20 md:w-28 md:h-28 rounded-full object-cover border-4 border-cyan-500/50 shadow-lg shadow-cyan-500/30" data-id="element-63" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-white" style={{
              fontFamily: 'Oswald, sans-serif'
            }} data-id="element-64">
                Brenden Henry
              </h3>
              <p className="text-base md:text-lg font-semibold text-cyan-400" data-id="element-65">
                #1 Top Peptide Authority
              </p>
            </div>
          </div>
        </motion.div>
        <p className="text-lg md:text-xl text-gray-300 max-w-4xl mx-auto font-light leading-relaxed" data-id="element-66">
          In a single session we'll show you the{' '}
          <span className="text-cyan-400 font-bold relative" data-id="element-67">
            EXACT steps
            <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-cyan-400 to-purple-400 blur-sm" data-id="element-68" />
          </span>{' '}
          to fix your issues and get the results you've been looking for. No
          more guesswork. No more going in circles. Just clarity, confidence and
          relief.
        </p>
        {/* CTA Button */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 2,
        duration: 0.6
      }} className="flex flex-col sm:flex-row gap-6 justify-center pt-8 pb-4" data-id="element-69">
          <motion.a whileHover={{
          scale: 1.05,
          y: -5
        }} whileTap={{
          scale: 0.98
        }} href="https://go.peptidescienceinstitute.com/mwm" className="relative px-12 py-6 bg-gradient-to-r from-purple-600 to-cyan-600 text-white text-xl font-bold rounded-xl overflow-hidden group shadow-2xl shadow-purple-500/50" data-id="element-70">
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-71" />
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-72">
              <div className="absolute inset-0 bg-white/20 blur-xl animate-pulse" data-id="element-73" />
            </div>
            <span className="relative z-10 tracking-wide" data-id="element-74">
              Book Your Meeting
            </span>
          </motion.a>
        </motion.div>
      </motion.div>
    </div>;
}